import { useEffect, useMemo, useState } from "react";
import { errorMessage } from "../api/client";
import { formatBrl, useUpdateAppTask } from "../api/appTasks";
import type { AppTaskRead, EnvironmentTypeRead, TaskCategoryRead } from "../api/types";
import { formatDate } from "../lib/format";
import { Switch, envColor } from "./AppTaskControls";

// Edit drawer for one task: environment (through the category), category, hourly rate,
// highlight and active flags, plus the texts. Only the fields the admin changed are sent,
// so an untouched rate is never overwritten and `payments_value: null` means "clear".

interface Props {
  task: AppTaskRead | null; // null = closed
  environments: EnvironmentTypeRead[];
  categories: TaskCategoryRead[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

export default function AppTaskDrawer({ task, environments, categories, onClose, onSaved }: Props) {
  const update = useUpdateAppTask();
  const [envSlug, setEnvSlug] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [pay, setPay] = useState("");
  const [featured, setFeatured] = useState(false);
  const [active, setActive] = useState(true);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");

  // (re)load the form whenever a different task is opened
  useEffect(() => {
    if (!task) return;
    setEnvSlug(task.environment_slug);
    setCategoryId(task.task_category_id);
    setPay(task.payments_value == null ? "" : task.payments_value.toFixed(2));
    setFeatured(task.is_featured);
    setActive(task.is_active);
    setTitle(task.title);
    setDescription(task.description);
    setTitleEn(task.title_en);
    setDescriptionEn(task.description_en);
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  useEffect(() => {
    if (!task) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [task, onClose]);

  const envCategories = useMemo(
    () => categories.filter((c) => c.environment_slug === envSlug),
    [categories, envSlug],
  );

  // switching environment: keep the category if that environment has the same slug, else first
  function pickEnv(slug: string) {
    setEnvSlug(slug);
    const current = categories.find((c) => c.id === categoryId);
    const next =
      categories.find((c) => c.environment_slug === slug && c.slug === current?.slug) ??
      categories.find((c) => c.environment_slug === slug && c.is_active) ??
      categories.find((c) => c.environment_slug === slug);
    setCategoryId(next?.id ?? "");
  }

  if (!task) return <div className="drawer-scrim" />;

  const willDisable = task.is_active && !active;
  const payNumber = pay.trim() === "" ? null : Number(pay);
  const payInvalid = payNumber != null && (!Number.isFinite(payNumber) || payNumber < 0);

  function save() {
    if (!task || payInvalid || !categoryId) return;
    const body: Parameters<typeof update.mutate>[0]["body"] = {};
    if (categoryId !== task.task_category_id) body.task_category_id = categoryId;
    if (payNumber !== task.payments_value) body.payments_value = payNumber;
    if (featured !== task.is_featured) body.is_featured = featured;
    if (active !== task.is_active) body.is_active = active;
    if (title.trim() !== task.title) body.title = title.trim();
    if (description.trim() !== task.description) body.description = description.trim();
    if (titleEn.trim() !== task.title_en) body.title_en = titleEn.trim();
    if (descriptionEn.trim() !== task.description_en) body.description_en = descriptionEn.trim();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    update.mutate(
      { id: task.id, body },
      {
        onSuccess: (saved) => {
          onSaved(
            willDisable
              ? `“${saved.title}” disabled — hidden from the app`
              : `“${saved.title}” saved`,
          );
          onClose();
        },
      },
    );
  }

  return (
    <>
      <div className="drawer-scrim open" onClick={onClose} />
      <aside className="drawer open at-drawer" role="dialog" aria-modal="true" aria-label="Edit task">
        <div className="drawer-head">
          <div className="drawer-head-text">
            <h2>{task.title}</h2>
            <div className="at-slug">{task.id}</div>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="drawer-body at-form">
          <div className="at-field">
            <span className="field-label">Environment</span>
            <div className="at-radios">
              {environments.map((e) => (
                <label key={e.id} style={{ ["--sc" as string]: envColor(e.slug) }}>
                  <input
                    type="radio"
                    name="at-env"
                    value={e.slug}
                    checked={envSlug === e.slug}
                    onChange={() => pickEnv(e.slug)}
                  />
                  {e.name_en}
                  <small>{e.name}</small>
                </label>
              ))}
            </div>
            <div className="at-hint">
              Moving the task to the other environment changes which categories are available.
            </div>
          </div>

          <div className="at-field">
            <label className="field-label" htmlFor="at-d-cat">
              Category
            </label>
            <select
              id="at-d-cat"
              className="select-input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              {envCategories.length === 0 && <option value="">No categories in this environment</option>}
              {envCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.name_en}
                  {c.is_active ? "" : " (inactive)"}
                </option>
              ))}
            </select>
          </div>

          <div className="at-field">
            <label className="field-label" htmlFor="at-d-pay">
              Hourly rate (payments_value)
            </label>
            <div className={payInvalid ? "at-moneyin invalid" : "at-moneyin"}>
              <span>R$</span>
              <input
                id="at-d-pay"
                className="text-input"
                type="number"
                min={0}
                step={0.5}
                placeholder="0,00"
                value={pay}
                onChange={(e) => setPay(e.target.value)}
              />
              <span>/ hour</span>
            </div>
            <div className="at-hint">
              Base for the user's payment on recordings of this task. Leave empty to clear it.
            </div>
          </div>

          <div className="at-rowfield">
            <div>
              <div className="at-rowfield-t">Highlight in the app</div>
              <div className="at-rowfield-d">Shown at the top of the list with a highlight badge.</div>
            </div>
            <Switch checked={featured} onChange={setFeatured} label="Highlight in the app" />
          </div>
          <div className="at-rowfield">
            <div>
              <div className="at-rowfield-t">Task active</div>
              <div className="at-rowfield-d">
                Disabling removes the task from the app list. Past recordings are not affected.
              </div>
            </div>
            <Switch checked={active} onChange={setActive} label="Task active" />
          </div>
          {willDisable && (
            <div className="at-notice">
              On save, <code>last_disable_at</code> is stamped with the current date and time.
            </div>
          )}

          <div className="at-field">
            <label className="field-label" htmlFor="at-d-title">
              Title <span className="at-lang">PT-BR</span>
            </label>
            <input
              id="at-d-title"
              className="text-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="at-field">
            <label className="field-label" htmlFor="at-d-desc">
              Description <span className="at-lang">PT-BR</span>
            </label>
            <textarea
              id="at-d-desc"
              className="text-input at-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="at-field">
            <label className="field-label" htmlFor="at-d-title-en">
              Title <span className="at-lang">EN</span>
            </label>
            <input
              id="at-d-title-en"
              className="text-input"
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
            />
          </div>
          <div className="at-field">
            <label className="field-label" htmlFor="at-d-desc-en">
              Description <span className="at-lang">EN</span>
            </label>
            <textarea
              id="at-d-desc-en"
              className="text-input at-textarea"
              value={descriptionEn}
              onChange={(e) => setDescriptionEn(e.target.value)}
            />
          </div>

          <dl className="at-meta">
            <div>
              <dt>Last disabled</dt>
              <dd>{task.last_disable_at ? formatDate(task.last_disable_at) : "—"}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDate(task.updated_at)}</dd>
            </div>
            <div>
              <dt>Current rate</dt>
              <dd>{formatBrl(task.payments_value) ?? "not set"}</dd>
            </div>
            <div>
              <dt>Sort order</dt>
              <dd>{task.sort_order}</dd>
            </div>
          </dl>

          {update.isError && <p className="error">{errorMessage(update.error)}</p>}
        </div>

        <div className="drawer-foot">
          <button className="btn ghost" onClick={onClose} disabled={update.isPending}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={save}
            disabled={update.isPending || payInvalid || !categoryId || !title.trim() || !titleEn.trim()}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </aside>
    </>
  );
}
